/**
 * CorporateDashPage — V2 corporate dashboard with 2 tabs:
 * Campus Review (stats + shift + hourly chart) and Campus Trends (12-week + KPI switching).
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import { getSession, getCampusId } from '../../../services/session.js';
import {
    fetchCorpStats, fetchCorpTrends, fetchCorpHourChart, fetchCorpFilterOptions, fetchShiftHours,
} from './api/corpDashApi.js';

import CampusReviewTable from './components/CampusReviewTable.jsx';
import PerformanceCards from './components/PerformanceCards.jsx';
import CampusTrendsTable from './components/CampusTrendsTable.jsx';
import CorpHourlyChart from './components/CorpHourlyChart.jsx';
import CorpTrendChart from './components/CorpTrendChart.jsx';
import CorpFilterDrawer from './components/CorpFilterDrawer.jsx';
import ShiftLegend from './components/ShiftLegend.jsx';

import './corporate-dash-page.css';

const CorporateDashPage = () => {
    const session = useMemo(() => getSession(), []);
    const corpId = session?.corp;

    // ======================================================================
    // TAB STATE
    // ======================================================================
    const [activeTab, setActiveTab] = useState('review');

    // ======================================================================
    // FILTER STATE
    // ======================================================================
    const [areaTypeOptions, setAreaTypeOptions] = useState([]);
    const [deviceCatOptions, setDeviceCatOptions] = useState([]);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [filters, setFilters] = useState({ areaTypes: '', deviceCategories: '', responseTime: [false, 'lt', 60] });

    // ======================================================================
    // DATA STATE
    // ======================================================================
    const [statsData, setStatsData] = useState([]);
    const [compareData, setCompareData] = useState({});
    const [trendsData, setTrendsData] = useState([]);
    const [trendsWeeks, setTrendsWeeks] = useState({});
    const [hourData, setHourData] = useState({});
    const [loading, setLoading] = useState(true);
    const hasLoadedRef = useRef(false);

    // ======================================================================
    // CAMPUS SELECTION STATE
    // ======================================================================
    const [campusId, setCampusId] = useState(null);
    const [campusName, setCampusName] = useState('');
    const [campusTotal, setCampusTotal] = useState(0);
    const [campusAvgD, setCampusAvgD] = useState(0);
    const [campusAvgR, setCampusAvgR] = useState(0);
    const [shiftData, setShiftData] = useState([]);

    // ======================================================================
    // TRENDS STATE
    // ======================================================================
    const [kpi, setKpi] = useState('response');
    const [reviewSearch, setReviewSearch] = useState('');
    const [trendsSearch, setTrendsSearch] = useState('');

    // ======================================================================
    // TOAST
    // ======================================================================
    const [toast, setToast] = useState(null);
    const toastTimerRef = useRef(null);
    const showToast = useCallback((type, message) => {
        clearTimeout(toastTimerRef.current);
        setToast({ type, message });
        toastTimerRef.current = setTimeout(() => setToast(null), 4000);
    }, []);
    useEffect(() => () => clearTimeout(toastTimerRef.current), []);

    // ======================================================================
    // LOAD FILTER OPTIONS
    // ======================================================================
    useEffect(() => {
        if (!corpId) return;
        fetchCorpFilterOptions(corpId).then((data) => {
            setAreaTypeOptions(data?.area_types || []);
            setDeviceCatOptions(data?.device_categories || []);
        });
    }, [corpId]);

    // ======================================================================
    // LOAD DASHBOARD DATA
    // ======================================================================
    const loadData = useCallback(async () => {
        if (!corpId) return;
        try {
            const [stats, trends] = await Promise.all([
                fetchCorpStats(corpId, filters),
                fetchCorpTrends(corpId, filters),
            ]);
            const records = stats?.records || [];
            setStatsData(records);
            setCompareData(stats?.compare || {});

            // Auto-select first campus if none selected
            if (records.length > 0 && campusId === null) {
                selectCampus(records[0], stats?.compare);
            }

            const trendRecords = trends?.records || [];
            setTrendsData(trendRecords);
            // Extract week date ranges
            const weeks = {};
            for (let i = 1; i <= 12; i++) {
                weeks[`week${i}_start`] = trends?.[`week${i}_start`] ? formatMMDD(trends[`week${i}_start`]) : '';
                weeks[`week${i}_end`] = trends?.[`week${i}_end`] ? formatMMDD(trends[`week${i}_end`]) : '';
            }
            setTrendsWeeks(weeks);
        } catch (err) {
            console.error('[CorporateDashPage] loadData error:', err);
            showToast('error', 'Failed to load corporate dashboard');
        } finally {
            if (!hasLoadedRef.current) {
                hasLoadedRef.current = true;
                setLoading(false);
            }
        }
    }, [corpId, filters, showToast]);

    useEffect(() => { loadData(); }, [loadData]);

    // Polling: 600s
    useEffect(() => {
        const timer = setInterval(loadData, 600000);
        return () => clearInterval(timer);
    }, [loadData]);

    // ======================================================================
    // LOAD HOURLY CHART + SHIFT DATA ON CAMPUS SELECTION
    // ======================================================================
    useEffect(() => {
        if (campusId === null || !corpId) return;
        fetchCorpHourChart(corpId, campusId, filters).then((data) => {
            setHourData(data || {});
        });
        // Fetch shift hours — campus-specific, fall back to corporate defaults
        fetchShiftHours(campusId === 0 ? null : campusId).then((data) => {
            if (Array.isArray(data) && data.length > 0) {
                setShiftData(data);
            } else if (campusId !== 0) {
                // Fallback to corporate defaults
                fetchShiftHours(null).then((defaults) => setShiftData(Array.isArray(defaults) ? defaults : []));
            }
        });
    }, [campusId, corpId, filters]);

    // ======================================================================
    // HANDLERS
    // ======================================================================
    const selectCampus = useCallback((row, compare) => {
        const id = row.campus_id;
        setCampusId(id);
        setCampusName(row.campus_name || '');
        // Look up stats from statsData (trends rows may not have these fields)
        const statsRow = statsData.find((r) => r.campus_id === id) || row;
        setCampusTotal(statsRow.total_events ?? row.total_events);
        setCampusAvgD(statsRow.avg_daily_events ?? row.avg_daily_events);
        setCampusAvgR(statsRow.avg_response_time ?? row.avg_response_time);
        if (compare) {
            setCompareData(compare);
        }
    }, [statsData]);

    const handleSelectCampus = useCallback((row) => {
        selectCampus(row, compareData);
    }, [selectCampus, compareData]);

    const handleApplyFilters = useCallback((newFilters) => {
        setFilters(newFilters);
        setFilterDrawerOpen(false);
    }, []);

    // Selected campus trend data
    const selectedTrendCampus = useMemo(() => {
        if (!campusId) return null;
        return trendsData.find((r) => r.campus_id === campusId) || null;
    }, [trendsData, campusId]);

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (filters.areaTypes) count++;
        if (filters.deviceCategories) count++;
        if (filters.responseTime?.[0]) count++;
        return count;
    }, [filters]);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="md-page">
                    {/* Toast */}
                    {toast ? (
                        <div className={`dp-toast dp-toast--${toast.type}`} role="status" aria-live="polite">
                            <span>{toast.message}</span>
                            <button onClick={() => setToast(null)} className="dp-toast__close">{'\u2715'}</button>
                        </div>
                    ) : null}

                    {/* Header */}
                    <header className="dp-page-header">
                        <div className="dp-page-header__inner">
                            <div className="dp-breadcrumb-row">
                                <nav className="dp-breadcrumb">
                                    <span className="dp-breadcrumb__text">Dashboards</span>
                                    <span className="dp-breadcrumb__separator">/</span>
                                    <span className="dp-breadcrumb__current">Corporate</span>
                                </nav>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--space-2)' }}>
                                <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-heading)', margin: 0 }}>Corporate Dashboard</h1>
                                <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => setFilterDrawerOpen(true)} style={{ position: 'relative' }}>
                                    <i className="fe fe-filter" style={{ marginRight: 4 }} />Filter
                                    {activeFilterCount > 0 ? <span className="ar-filter-badge">{activeFilterCount}</span> : null}
                                </button>
                            </div>
                        </div>
                    </header>

                    {/* Tab bar */}
                    <div className="pp-tab-bar">
                        <button className={`pp-tab${activeTab === 'review' ? ' is-active' : ''}`} onClick={() => setActiveTab('review')}>Campus Review</button>
                        <button className={`pp-tab${activeTab === 'trends' ? ' is-active' : ''}`} onClick={() => setActiveTab('trends')}>Campus Trends</button>
                    </div>

                    {/* Content */}
                    <div className="md-content">
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-text-muted)' }}>
                                <div className="ar-loading__spinner" style={{ margin: '0 auto var(--space-3)' }} />
                                <p>Loading corporate dashboard...</p>
                            </div>
                        ) : null}

                        {/* Campus Review Tab */}
                        {!loading && activeTab === 'review' ? (
                            <div className="cd-split">
                                <CampusReviewTable
                                    data={statsData}
                                    selectedCampusId={campusId}
                                    onSelectCampus={handleSelectCampus}
                                    search={reviewSearch}
                                    onSearchChange={setReviewSearch}
                                    shiftData={shiftData}
                                />

                                {campusId !== null ? (
                                    <>
                                        <PerformanceCards
                                            campusName={campusName}
                                            campusTotal={campusTotal}
                                            campusAvgD={campusAvgD}
                                            campusAvgR={campusAvgR}
                                            compareTotal={compareData?.total_events}
                                            compareAvgD={compareData?.avg_daily_events}
                                            compareAvgR={compareData?.avg_response_time}
                                        />
                                        <CorpHourlyChart chartData={hourData} campusName={campusName} />
                                    </>
                                ) : null}
                            </div>
                        ) : null}

                        {/* Campus Trends Tab */}
                        {!loading && activeTab === 'trends' ? (
                            <div className="cd-split">
                                <CampusTrendsTable
                                    data={trendsData}
                                    weeks={trendsWeeks}
                                    selectedCampusId={campusId}
                                    onSelectCampus={handleSelectCampus}
                                    kpi={kpi}
                                    onKpiChange={setKpi}
                                    search={trendsSearch}
                                    onSearchChange={setTrendsSearch}
                                />
                                <CorpTrendChart
                                    campusData={selectedTrendCampus}
                                    weeks={trendsWeeks}
                                    kpi={kpi}
                                    campusName={campusName}
                                />
                            </div>
                        ) : null}
                    </div>

                    {/* Filter Drawer */}
                    <CorpFilterDrawer
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        onApply={handleApplyFilters}
                        areaTypeOptions={areaTypeOptions}
                        deviceCatOptions={deviceCatOptions}
                        currentAreaTypes={filters.areaTypes ? filters.areaTypes.split(',').filter(Boolean) : []}
                        currentDeviceCats={filters.deviceCategories ? filters.deviceCategories.split(',').filter(Boolean) : []}
                        currentResponseTime={filters.responseTime}
                    />
                </div>
            </div>
        </>
    );
};

/** Format ISO date string to MM/DD */
function formatMMDD(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

export default CorporateDashPage;
